import Handlebars from "../handlebars/handlebars";
import errorResponseHandler from "../errors/errorResponseHandler";
import filesUploadingHtml from "./filesUploading.hbs";
import instantProofHtml from "./instantProof.hbs";
import addFilesHtml from "./addFiles.hbs";
import filesUploadedHtml from "./filesUploaded.hbs";

const CLOSE_ACTION = '.js-action--close';
const FILE_INPUT = '.js-element--fileInput';
const FILE_INPUT_CTA = '.js-element--fileUploadContainer';
const FILE_REMOVE = '.js-action--removeUploadedFile';
const FILE_REMOVE_CANCEL = '.js-action--undoRemoveFile';
const FILE_UPLOAD_CANCEL = '.js-action--cancelFileUpload';
const FILES_UPLOADING = '.js-element--uploadingFiles';
const FILES_UPLOADED = '.js-element--uploadedFiles';
const UPLOAD_LIST_ITEM = '.js-element--fileUploadItem';
const UPLOAD_FILE_PROGRESS = '.js-element--fileUploadProgress';
const CSS_FILE_DRAGOVER = 'hasDragOver';

class AddFilesController {
    constructor (modalInstance, container, currentItem, getProject) {
        this.isInstantProof = Boolean(currentItem.fileSessionId);

        this.state = {
            loading: false,
            currentItem: currentItem,
            undoPrintFileIds: [],
            currentFileUploadList: null,
            versionsPrintFilePages: currentItem.configDetails.versions.length * (currentItem.isTwoSided ? 2 : 1),
            hasPages: currentItem.pages > 0,
        };

        this.modalInstance = modalInstance;
        this.container = container;
        this.getProject = getProject;
        this.template = this.isInstantProof ? instantProofHtml : addFilesHtml;

        this.handleClick = this.handleClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.disableLoadingState = this.disableLoadingState.bind(this);
        this.handleUploadItemRemove = this.handleUploadItemRemove.bind(this);
        this.renderUploadedFilesList = this.renderUploadedFilesList.bind(this);

        this.deletePrintFilesTimeoutIDs = {};

        !this.isInstantProof && this.enableUI();
        this.render()
    }

    enableUI () {
        this.container.addEventListener('click', this.handleClick, false);
        this.container.addEventListener('change', this.handleChange, false);
        this.container.addEventListener('dragenter', this.handleFileDragEnter, false);
        this.container.addEventListener('dragover', this.handleFileDragOver, false);
        this.container.addEventListener('dragleave', this.handleFileDragOff, false);
        this.container.addEventListener('drop', this.handleFileDragDrop, false);
        return this;
    }

    render () {
        this.container.innerHTML = this.template(this.state);

        if (!this.isInstantProof) {
            this.renderUploadedFilesList();
            this.renderFilesUploadingList();
        }
    }

    renderFilesUploadingList () {
        this.container.querySelector(FILES_UPLOADING).innerHTML = filesUploadingHtml(this.state.currentFileUploadList);
    }

    renderUploadedFilesList () {
        this.container.querySelector(FILES_UPLOADED).innerHTML = filesUploadedHtml(this.state);
    }

    handleFileDragEnter (e) {
        if (e.target.matches(FILE_INPUT)) {
            e.target.closest(FILE_INPUT_CTA).classList.add(CSS_FILE_DRAGOVER);
        }
    }

    handleFileDragOver (e) {
        if (e.target.matches(FILE_INPUT)) {
            e.target.closest(FILE_INPUT_CTA).classList.add(CSS_FILE_DRAGOVER);
        }
    }

    handleFileDragOff (e) {
        if (e.target.matches(FILE_INPUT)) {
            e.target.closest(FILE_INPUT_CTA).classList.remove(CSS_FILE_DRAGOVER);
        }
    }

    handleFileDragDrop (e) {
        if (e.target.matches(FILE_INPUT)) {
            e.target.closest(FILE_INPUT_CTA).classList.remove(CSS_FILE_DRAGOVER);
        }
    }

    handleClick (e) {
        if (e.target.matches(FILE_REMOVE)) {
            return this.delayFileRemove(e);
        }

        if (e.target.matches(FILE_REMOVE_CANCEL)) {
            return this.clearFileRemove(e);
        }

        if (e.target.matches(FILE_UPLOAD_CANCEL)) {
            return this.handleCancelFileUpload(e);
        }

        if (e.target.matches(CLOSE_ACTION)) {
            return this.getProject()
                .then(this.modalInstance.close)
                .catch(errorResponseHandler.generateErrorHandler());
        }
    }

    handleChange (e) {
        if (e.target.matches(FILE_INPUT)) {
            return this.handleFileInputChange(e);
        }
    }

    delayFileRemove (e) {
        const printFileId = Number(e.target.closest(UPLOAD_LIST_ITEM).dataset.fileId);
        const printFile = this.state.currentItem.getPrintFileById(printFileId);

        if (this.state.undoPrintFileIds.includes(printFileId)) {
            return;
        }

        this.state.undoPrintFileIds.push(printFileId);
        this.renderUploadedFilesList();
        this.deletePrintFilesTimeoutIDs[printFileId] = window.setTimeout(this.handleUploadItemRemove, 3000, printFile);
    }

    clearFileRemove (e) {
        const printFileId = Number(e.target.closest(UPLOAD_LIST_ITEM).dataset.fileId);

        if (!this.state.undoPrintFileIds.includes(printFileId)) {
            return;
        }

        this.state.undoPrintFileIds.splice(this.state.undoPrintFileIds.indexOf(printFileId), 1);
        this.renderUploadedFilesList();
        window.clearTimeout(this.deletePrintFilesTimeoutIDs[printFileId]);
    }

    handleUploadItemRemove (printFile) {
        this.enableLoadingState();

        Collaterate.Assets.deleteOrderItemPrintFile(printFile)
            .then(this.getProject)
            .then(project => this.state.currentItem = project.getItemById(this.state.currentItem.id))
            .then(this.renderUploadedFilesList)
            .catch(errorResponseHandler.generateErrorHandler())
            .finally(this.disableLoadingState);
    }

    handleCancelFileUpload (e) {
        const uploadingFileID = e.target.closest(UPLOAD_FILE_PROGRESS).dataset.itemId;
        const uploadingFile = this.state.currentFileUploadList.getUploadById(uploadingFileID);
        e.preventDefault();
        uploadingFile.cancel();
    }

    handleFileInputChange (e) {
        const files = e.target.files;

        this.fileTypeValidation(files);

        this.enableLoadingState();

        this.state.currentFileUploadList = Collaterate.Assets.uploadOrderItemPrintFiles(this.state.currentItem, files)
            .change((fileUploadList, fileUpload) => {
                if (!fileUpload) {
                    return;
                    // fileUpload is null when complete so we dun here.
                }

                this.renderFilesUploadingList()

                if (fileUpload.complete) {
                    this.getProject()
                        .then(project => this.state.currentItem = project.getItemById(this.state.currentItem.id))
                        .then(this.renderUploadedFilesList)
                        .catch(errorResponseHandler.generateErrorHandler())
                }
            })
            .complete(() => {
                this.state.currentFileUploadList = null;
                this.disableLoadingState()
            });

        this.container.querySelector(FILE_INPUT).value = ''; // support for iOS thumbnail removal on a fileInput element
    }

    fileTypeValidation (files) {
        const fileTypes = {
            'xsl': 'application/vnd.ms-excel',
            'indd': 'application/postscript'
        }

        for (var i = 0; i < files.length; i++) {
            var fileExt = files[i].name.split('.').pop();

            if (fileTypes[fileExt]) {
                Object.defineProperty(files[i], 'type', { value: fileTypes[fileExt] });
            }
        }
    }

    enableLoadingState () {
        this.state.loading = true;
        this.render();
    }

    disableLoadingState () {
        this.state.loading = false;
        this.render();
    }
}
export default AddFilesController;